import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { formatDate } from '@/utils/date';
import type { ModelServiceLicense } from '@/types/license';

type ModelServiceState = 'opened' | 'refused' | 'previousLicense' | 'waiting';

const STATE_VARIANTS: Record<ModelServiceState, BadgeVariant> = {
    opened: 'positive',
    refused: 'negative',
    previousLicense: 'base',
    waiting: 'warning',
};

function stateOf(service: ModelServiceLicense): ModelServiceState {
    if (service.usesCurrentLicense) {
        return service.decrypted ? 'opened' : 'refused';
    }
    return service.decrypted ? 'previousLicense' : 'waiting';
}

export function ModelServicesSection({
    services,
}: {
    services: ModelServiceLicense[];
}) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);

    if (services.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-grey-300">
                    {t('license.modelServices.section')}
                </span>
                <span className="text-xs text-grey-300">
                    {t('license.modelServices.description')}
                </span>
            </div>
            {services.map((service) => {
                const state = stateOf(service);
                return (
                    <div
                        key={service.service}
                        className="border border-grey-600 rounded-[10px] p-4 flex flex-col gap-2"
                    >
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm text-grey-200">
                                {t(
                                    `license.modelServices.names.${service.service}`,
                                    { defaultValue: service.service },
                                )}
                            </span>
                            <Badge variant={STATE_VARIANTS[state]}>
                                {t(`license.modelServices.states.${state}`)}
                            </Badge>
                        </div>
                        <span className="text-xs text-grey-300">
                            {t(`license.modelServices.hints.${state}`, {
                                time: formatDate(service.checkedAtEpochSec),
                            })}
                        </span>
                        {!service.decrypted && service.reason ? (
                            <span className="text-xs text-danger-200 whitespace-pre-line">
                                {service.reason}
                            </span>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
